import { ResponsiveTable, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { MoreMenu } from '../../_ops/MoreMenu.js';
import type { UserListItemDto } from '../contractsMirror.js';
import { UserStatusBadge } from './UserStatusBadge.js';

/**
 * People & Access list table (legacy `UsersPage` table, redesigned to the locked brand —
 * archetype A list). A real semantic `<table>` — Name · Email · Role · Linked Employee ·
 * Status (badge) · Actions — with a `<caption>`, `<th scope="col">` headers, and a
 * readable empty cell (A11Y-01). Every color is a locked token (BRAND-06).
 *
 * The name cell links to the edit route. The row-action `MoreMenu` (Shared `_ops`) offers
 * Edit / Deactivate-or-Restore; the destructive item routes through the caller's
 * `ConfirmDialog` (Shared 7 — never a native confirm).
 */

const TEXT_TH =
  'px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const NUMERIC_TH =
  'px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide';

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

export function UsersTable({
  users,
  onEdit,
  onDeactivate,
}: {
  users: UserListItemDto[];
  /** In-SPA navigation to the user edit route (router `navigate`). */
  onEdit: (user: UserListItemDto) => void;
  /** The caller opens its `ConfirmDialog` for the destructive deactivate/restore action. */
  onDeactivate: (user: UserListItemDto) => void;
}) {
  const { t } = useTranslation();

  return (
    <ResponsiveTable className="rounded-md border border-border bg-card">
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('users:table.caption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className={TEXT_TH}>
              {t('users:table.name')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('users:table.email')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('users:table.role')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('users:table.linkedEmployee')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('users:table.status')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('users:table.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} className="border-border/60 border-b last:border-b-0">
              <td className="px-3 py-2 text-sm">
                <Link
                  to="/users/$id"
                  params={{ id: String(user.id) }}
                  className="font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {user.name}
                </Link>
              </td>
              <td className="px-3 py-2 text-foreground text-sm">{user.email}</td>
              <td className="px-3 py-2 text-foreground text-sm">{t(`users:roles.${user.role}`)}</td>
              <td className="px-3 py-2 text-foreground text-sm">
                {user.employeeName ? user.employeeName : <Dash />}
              </td>
              <td className="px-3 py-2 text-sm">
                <UserStatusBadge user={user} />
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex justify-end">
                  <MoreMenu
                    label={t('users:actions.menuLabel', { name: user.name })}
                    items={[
                      {
                        key: 'edit',
                        label: t('users:actions.editShort'),
                        onSelect: () => onEdit(user),
                      },
                      {
                        key: 'deactivate',
                        label:
                          user.active === 1
                            ? t('users:actions.deactivate')
                            : t('users:actions.restore'),
                        destructive: user.active === 1,
                        onSelect: () => onDeactivate(user),
                      },
                    ]}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}
